import { ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useShop } from '../../../shared/store';
import { sortProductsByAudienceFace } from '../../../shared/utils/bookAudience';
import HomeHeroCarousel from '../components/HomeHeroCarousel';

const teamMembers = [
  {
    name: 'Mudacumura Fiston',
    role: 'Founder',
    image: '/WhatsApp_Image_2026-05-22_at_21.02.52-removebg-preview.png',
  },
  {
    name: 'Mushikiwabo Bat Belite',
    role: 'Backend Developer',
    image: '/a38b76a2832e89a3e132d2058a387385-removebg-preview.png',
  },
  {
    name: 'Iyaremye Dushime Keza Kevin',
    role: 'Frontend Developer',
    image: '/WhatsApp Image 2026-03-28 at 20.22.03.jpeg.png',
  },
  {
    name: 'Manzi Vaillant',
    role: 'Backend Developer',
    image: '/WhatsApp_Image_2026-04-07_at_18.01.26-removebg-preview.png',
  },
];

const SIGNIN_POPUP_DISMISS_KEY = 'haapa.signin-popup-dismissed-at';
const SIGNIN_POPUP_COOLDOWN_MS = 5 * 60 * 1000;

const clampToNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  return fallback;
};

const HomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { products, currentUser, themeMode } = useShop();
  const isDark = themeMode === 'dark';

  const slideCardRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const popupTimerRef = useRef<number | null>(null);

  const [heroSearch, setHeroSearch] = useState('');
  const [isSigninPromptOpen, setIsSigninPromptOpen] = useState(false);
  const [activeSlideBookId, setActiveSlideBookId] = useState('');

  const mixedProducts = useMemo(() => sortProductsByAudienceFace(products, 'public'), [products]);

  const filteredPublicProducts = useMemo(() => {
    const normalizedQuery = heroSearch.trim().toLowerCase();
    return mixedProducts
      .filter((product) => {
        if (!normalizedQuery) {
          return true;
        }
        return (
          t(product.nameKey).toLowerCase().includes(normalizedQuery) ||
          t(product.descriptionKey).toLowerCase().includes(normalizedQuery)
        );
      })
      .slice(0, 18);
  }, [heroSearch, mixedProducts, t]);

  const sliderItems = useMemo(() => filteredPublicProducts.slice(0, 10), [filteredPublicProducts]);
  const activeSlideBook = sliderItems.find((product) => product.id === activeSlideBookId) ?? sliderItems[0];
  const activeSlideIndex = activeSlideBook ? sliderItems.findIndex((product) => product.id === activeSlideBook.id) : -1;

  useEffect(() => {
    if (sliderItems.length < 2) {
      return undefined;
    }

    const autoplay = window.setInterval(() => {
      setActiveSlideBookId((previousId) => {
        const currentIndex = sliderItems.findIndex((product) => product.id === previousId);
        const fallbackIndex = currentIndex === -1 ? 0 : currentIndex;
        const nextIndex = (fallbackIndex + 1) % sliderItems.length;
        return sliderItems[nextIndex]?.id ?? previousId;
      });
    }, 3600);

    return () => window.clearInterval(autoplay);
  }, [sliderItems]);

  useEffect(() => {
    if (popupTimerRef.current) {
      window.clearTimeout(popupTimerRef.current);
      popupTimerRef.current = null;
    }

    if (currentUser) {
      window.localStorage.setItem(SIGNIN_POPUP_DISMISS_KEY, String(Date.now()));
      if (isSigninPromptOpen) {
        window.setTimeout(() => setIsSigninPromptOpen(false), 0);
      }
      return undefined;
    }

    const dismissedAtRaw = window.localStorage.getItem(SIGNIN_POPUP_DISMISS_KEY);
    const dismissedAt = dismissedAtRaw ? Number(dismissedAtRaw) : 0;
    const hasValidDismissTime = Number.isFinite(dismissedAt) && dismissedAt > 0;
    const elapsedTime = hasValidDismissTime ? Date.now() - dismissedAt : SIGNIN_POPUP_COOLDOWN_MS;
    const canOpenNow = elapsedTime >= SIGNIN_POPUP_COOLDOWN_MS;

    if (canOpenNow) {
      popupTimerRef.current = window.setTimeout(() => {
        setIsSigninPromptOpen(true);
      }, 1300);

      return () => {
        if (popupTimerRef.current) {
          window.clearTimeout(popupTimerRef.current);
        }
      };
    }

    const remainingCooldown = SIGNIN_POPUP_COOLDOWN_MS - elapsedTime;
    popupTimerRef.current = window.setTimeout(() => {
      setIsSigninPromptOpen(true);
    }, remainingCooldown);

    return () => {
      if (popupTimerRef.current) {
        window.clearTimeout(popupTimerRef.current);
      }
    };
  }, [currentUser, isSigninPromptOpen]);

  const closeSigninPrompt = () => {
    setIsSigninPromptOpen(false);
    window.localStorage.setItem(SIGNIN_POPUP_DISMISS_KEY, String(Date.now()));
  };

  const shiftSlideBook = (direction: 'next' | 'prev') => {
    if (!sliderItems.length) {
      return;
    }

    const currentIndex = sliderItems.findIndex((product) => product.id === activeSlideBookId);
    const fallbackIndex = currentIndex === -1 ? 0 : currentIndex;
    const offset = direction === 'next' ? 1 : -1;
    const nextIndex = (fallbackIndex + offset + sliderItems.length) % sliderItems.length;
    const nextBook = sliderItems[nextIndex];
    if (!nextBook) {
      return;
    }

    setActiveSlideBookId(nextBook.id);
    slideCardRefs.current[nextBook.id]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  };

  return (
    <section className="bg-white">
      <HomeHeroCarousel />

      <section className="mx-auto w-full max-w-7xl px-2 pb-12 sm:px-4 lg:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-orange-500">
              {t('home.featured.overline', { defaultValue: 'Featured Mix' })}
            </p>
            <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">
              {t('home.featured.title', { defaultValue: 'Public Mixed Book Collection' })}
            </h2>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            <label className="relative w-full sm:w-[340px]">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={heroSearch}
                onChange={(event) => setHeroSearch(event.target.value)}
                placeholder={t('home.featured.searchPlaceholder', { defaultValue: 'Search featured books...' })}
                className="w-full rounded-2xl bg-white px-11 py-3 text-sm text-slate-900 shadow-sm transition focus:outline-none focus:ring-4 focus:ring-orange-100"
              />
              {heroSearch.trim() ? (
                <button
                  type="button"
                  onClick={() => setHeroSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                  aria-label={t('home.featured.clearSearchAria', { defaultValue: 'Clear search' })}
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </label>

            <Link to="/products" className="text-sm font-semibold text-orange-600 hover:text-orange-700">
              {t('home.featured.viewAll', { defaultValue: 'View all' })}
            </Link>
          </div>
        </div>

        <div className="mt-5 -mx-2 flex gap-3 overflow-x-auto px-2 pb-2 scroll-smooth [scrollbar-width:thin] [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-200">
          {filteredPublicProducts.map((product) => (
            <Link key={product.id} to={`/products/${product.id}`} className="group w-[152px] flex-none" aria-label={t(product.nameKey)}>
              <div className="overflow-hidden rounded-2xl bg-white shadow-sm transition group-hover:-translate-y-0.5 group-hover:shadow-md">
                <div className="relative aspect-[3/4] w-full bg-slate-100">
                  <img
                    src={product.imageUrl}
                    alt={t(product.nameKey)}
                    className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                </div>
                <div className="space-y-1 px-3 py-2.5">
                  <p className="line-clamp-1 text-sm font-semibold text-slate-900">{t(product.nameKey)}</p>
                  <p className="line-clamp-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                    {t(product.categoryKey)}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {activeSlideBook ? (
        <section className="mx-auto w-full max-w-7xl px-2 pb-14 sm:px-4 lg:px-6">
          <div className="overflow-hidden rounded-[30px] bg-white p-4 shadow-sm sm:p-6">
            <div className="space-y-6">
              <article
                className="relative overflow-hidden rounded-3xl p-6 text-white sm:p-7"
                style={{
                  backgroundImage: `linear-gradient(110deg, rgba(15,23,42,0.88) 0%, rgba(15,23,42,0.55) 45%, rgba(15,23,42,0.74) 100%), url('${activeSlideBook.imageUrl}')`,
                  backgroundPosition: 'center',
                  backgroundSize: 'cover',
                }}
              >
                <p className="inline-flex rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em]">
                  {t('home.slidingHighlight', { defaultValue: 'Sliding Book Highlight' })}
                </p>
                <h3 className="mt-4 text-3xl font-semibold">{t(activeSlideBook.nameKey)}</h3>
                <p className="mt-2 max-w-lg text-sm text-white/85">{t(activeSlideBook.descriptionKey)}</p>
                <div className="mt-4 flex flex-wrap items-center gap-3 text-xs font-semibold">
                  <span className="rounded-full bg-white/15 px-2 py-1">{t(activeSlideBook.categoryKey)}</span>
                  <span className="rounded-full bg-white/15 px-2 py-1">
                    {clampToNumber(activeSlideBook.rating, 0).toFixed(1)} rating
                  </span>
                  <span className="rounded-full bg-white/15 px-2 py-1">{clampToNumber(activeSlideBook.stock, 0)} in stock</span>
                </div>
                <div className="mt-6 flex flex-wrap gap-2">
                  <Link
                    to={`/products/${activeSlideBook.id}`}
                    className="rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600"
                  >
                    View Book
                  </Link>
                  <button
                    type="button"
                    onClick={() => void navigate('/products')}
                    className="rounded-xl bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/20"
                  >
                    See Full Catalog
                  </button>
                </div>
              </article>

              <article className="rounded-2xl bg-orange-50/35 p-3 sm:p-4">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <h4 className="text-lg font-semibold text-slate-900">Sliding Cards</h4>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                      Auto carousel{activeSlideIndex >= 0 ? ` - ${activeSlideIndex + 1}/${sliderItems.length}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 self-start">
                    <button
                      type="button"
                      onClick={() => shiftSlideBook('prev')}
                      className="rounded-full p-2 text-orange-700 transition hover:bg-orange-50"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => shiftSlideBook('next')}
                      className="rounded-full p-2 text-orange-700 transition hover:bg-orange-50"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="flex gap-3 overflow-x-auto pb-2 scroll-smooth snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {sliderItems.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      ref={(node) => {
                        slideCardRefs.current[product.id] = node;
                      }}
                      onClick={() => setActiveSlideBookId(product.id)}
                      className={`min-w-[144px] snap-start overflow-hidden rounded-xl bg-white text-left transition ${
                        product.id === activeSlideBook.id
                          ? 'ring-2 ring-orange-200'
                          : 'hover:-translate-y-0.5'
                      }`}
                    >
                      <div className="relative">
                        <img src={product.imageUrl} alt={t(product.nameKey)} className="h-32 w-full object-cover" />
                        {product.id === activeSlideBook.id ? (
                          <div className="absolute inset-x-1 bottom-1 rounded-full bg-slate-950/65 px-2 py-0.5 text-center text-[10px] font-semibold uppercase tracking-[0.12em] text-white">
                            Active
                          </div>
                        ) : null}
                      </div>
                      <div className="space-y-1 p-2">
                        <p className="line-clamp-1 text-xs font-semibold text-slate-900">{t(product.nameKey)}</p>
                        <p className="text-[11px] text-slate-500">$ {clampToNumber(product.price, 0).toFixed(2)}</p>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {sliderItems.map((product) => (
                    <button
                      key={`${product.id}-dot`}
                      type="button"
                      onClick={() => setActiveSlideBookId(product.id)}
                      className={`h-1.5 rounded-full transition ${
                        product.id === activeSlideBook.id ? 'w-8 bg-orange-500' : 'w-3 bg-slate-300 hover:bg-orange-300'
                      }`}
                      aria-label={`Select ${t(product.nameKey)}`}
                    />
                  ))}
                </div>
              </article>
            </div>
          </div>
        </section>
      ) : null}

      <section className="mx-auto w-full max-w-7xl px-2 pb-14 sm:px-4 lg:px-6">
        <div className="grid items-center gap-8 rounded-[32px] bg-white p-6 shadow-sm lg:grid-cols-[1fr_0.95fr] lg:p-8">
          <article className="space-y-4">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-orange-500">About Us</p>
            <h3 className="max-w-xl text-4xl font-semibold text-slate-900">We are building the future of African storytelling</h3>
            <p className="text-sm text-slate-600">
              Haapa combines books, audiobooks, and animation in one creative platform for readers, students, and artists.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <article className="rounded-2xl bg-orange-50 px-4 py-3">
                <p className="text-2xl font-semibold text-orange-700">94%</p>
                <p className="text-xs font-semibold text-slate-600">Reader Satisfaction</p>
              </article>
              <article className="rounded-2xl bg-orange-50 px-4 py-3">
                <p className="text-2xl font-semibold text-orange-700">65%</p>
                <p className="text-xs font-semibold text-slate-600">Monthly Returning Readers</p>
              </article>
            </div>
            <Link
              to="/about"
              className="inline-flex items-center rounded-xl bg-orange-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-700"
            >
              Learn More
            </Link>
          </article>
          <article className="relative">
            <div className="absolute inset-4 rounded-full bg-orange-100 blur-2xl" />
            <img
              src="/public/a7819fab7b443ba836ca1a315ac2902c.jpg"
              alt="About Haapa"
              className="relative h-80 w-full rounded-[26px] object-cover"
            />
            <div className="absolute bottom-4 left-4 rounded-full bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm">
              1.5k happy clients
            </div>
          </article>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-2 pb-14 sm:px-4 lg:px-6">
        <div className={`rounded-[34px] border px-6 py-10 shadow-[0_30px_80px_-60px_rgba(15,23,42,0.28)] sm:px-8 lg:px-10 ${
          isDark
            ? 'border-slate-800 bg-[linear-gradient(180deg,#0f172a_0%,#111827_100%)]'
            : 'border-slate-100 bg-[linear-gradient(180deg,#ffffff_0%,#fff8f5_100%)]'
        }`}>
          <p className="text-center text-xs font-semibold uppercase tracking-[0.24em] text-orange-600">Our Team</p>
          <h3 className={`mt-3 text-center text-4xl font-black tracking-tight sm:text-5xl ${isDark ? 'text-white' : 'text-slate-950'}`}>Meet the Team</h3>
          <p className={`mx-auto mt-4 max-w-3xl text-center text-sm leading-7 sm:text-base ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
            The team shaping Haapa's product vision and digital storytelling across Africa.
          </p>

          <div className="mt-10 grid gap-8 grid-cols-1 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-4">
            {teamMembers.map((member) => (
              <article key={member.name} className="group flex flex-col items-center">
                <div className="relative w-full max-w-[220px]">
                  <div className="absolute left-4 top-0 h-[78%] w-16 rounded-t-[2rem] rounded-b-lg bg-orange-500" />
                  <div className={`relative h-[300px] overflow-hidden rounded-[2rem] ${isDark ? 'ring-1 ring-white/10' : ''}`}> 
                    <img
                      src={member.image}
                      alt={member.name}
                      className="absolute inset-x-0 bottom-0 h-[94%] w-full object-cover object-top transition duration-500 group-hover:scale-[1.03]"
                    />
                    <div className={`absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t ${isDark ? 'from-orange-500 via-orange-500/90 to-transparent' : 'from-orange-500 via-orange-500/92 to-transparent'}`} />
                    <div className="absolute inset-x-0 bottom-0 px-4 pb-4 pt-10 text-white">
                      <h4 className="text-xl font-black tracking-tight">{member.name}</h4>
                      <p className="text-xs font-medium text-white/85">{member.role}</p>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <footer className="relative overflow-hidden bg-white">
        <div
          className="absolute inset-0 [background-image:radial-gradient(circle_at_20%_20%,rgba(249,115,22,0.18),transparent_52%),radial-gradient(circle_at_85%_10%,rgba(83,185,193,0.16),transparent_52%),radial-gradient(circle_at_55%_90%,rgba(251,146,60,0.10),transparent_60%)]"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 opacity-[0.45] [background-image:radial-gradient(rgba(15,23,42,0.12)_1px,transparent_1px)] [background-size:18px_18px]"
          aria-hidden="true"
        />
        <div className="pointer-events-none absolute -left-24 top-8 h-[420px] w-[420px] rounded-full bg-orange-400/20 blur-3xl haapa-glow-pulse" />
        <div className="pointer-events-none absolute -right-28 bottom-0 h-[480px] w-[480px] rounded-full bg-iw-teal/18 blur-3xl haapa-glow-pulse [animation-delay:1.2s]" />

        <div className="relative mx-auto max-w-7xl px-2 py-14 text-slate-900 sm:px-4 lg:px-6">
          <div className="grid gap-8 md:grid-cols-4">
            <article className="md:col-span-2">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600">Haapa Store</p>
              <h3 className="mt-3 text-4xl font-semibold">Read. Listen. Watch.</h3>
              <p className="mt-3 max-w-xl text-sm text-slate-600">
                A creative African storytelling platform where books, audio, and animation meet in one seamless experience.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <button className="rounded-xl bg-orange-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-700">
                  Join Community
                </button>
                <Link
                  to="/products"
                  className="rounded-xl bg-white px-5 py-3 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50"
                >
                  Explore Catalog
                </Link>
              </div>
            </article>
            <article className="space-y-2 text-sm text-slate-700">
              <h4 className="text-base font-semibold text-slate-900">Quick Links</h4>
              <Link to="/" className="block hover:text-orange-600">
                Home
              </Link>
              <Link to="/products" className="block hover:text-orange-600">
                Products
              </Link>
              <Link to="/animations" className="block hover:text-orange-600">
                Animations
              </Link>
              <Link to="/about" className="block hover:text-orange-600">
                About
              </Link>
              <Link to="/privacy-policy" className="block hover:text-orange-600">
                Privacy Policy
              </Link>
              <Link to="/terms-of-use" className="block hover:text-orange-600">
                Terms of Use
              </Link>
              <Link to="/auth" className="block hover:text-orange-600">
                Account
              </Link>
            </article>
            <article className="space-y-2 text-sm text-slate-700">
              <h4 className="text-base font-semibold text-slate-900">Contact</h4>
              <p>Kigali, Rwanda</p>
              <p>support@haapa.store</p>
              <p>+250 788 000 000</p>
              <p>Mon - Sat, 8:00 - 18:00</p>
            </article>
          </div>
          <div className="mt-10 space-y-2 pt-4 text-xs text-slate-500">
            <p>{t('home.footer.copyright')}</p>
            <p>{t('home.footer.credit')}</p>
          </div>
        </div>
      </footer>

      {!currentUser && isSigninPromptOpen ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/65 px-4 py-8 backdrop-blur-sm">
          <article className="relative w-full max-w-2xl overflow-hidden rounded-[30px] border border-white/45 bg-[#dff3ff] p-6 shadow-[0_40px_120px_-60px_rgba(0,0,0,0.9)] dark:border-slate-800 dark:bg-[#0b1220] sm:p-8">
            <button
              type="button"
              onClick={closeSigninPrompt}
              className="absolute right-4 top-4 z-20 rounded-full border border-slate-300 bg-white/75 p-2 text-slate-700 transition hover:bg-white dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-300 dark:hover:bg-slate-800"
              aria-label="Close sign in popup"
            >
              <X className="h-5 w-5" />
            </button>
            <p className="inline-flex rounded-full bg-white/85 dark:bg-slate-900/90 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-orange-700 dark:text-orange-500">
              Public Access
            </p>
            <h3 className="mt-4 text-3xl font-semibold leading-tight text-[#1f2f74] dark:text-slate-100">
              Sign in to unlock your personalized reading face
            </h3>
            <p className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-300">
              Kids and adults each get tailored homepages, recommendations, and product experiences after login.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void navigate('/auth')}
                className="rounded-xl bg-orange-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-700"
              >
                Sign In
              </button>
              <button
                type="button"
                onClick={closeSigninPrompt}
                className="rounded-xl border border-slate-300 bg-white/80 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-white dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Maybe Later
              </button>
            </div>
          </article>
        </div>
      ) : null}
    </section>
  );
};

export default HomePage;
